// Canonical field registry + alias resolver for applicant exports.
//
// This is the pragmatic slice of the "Master Field Library" architecture
// that fixes the actual pain point: duplicate export columns caused by
// different forms using different labels ("Name" vs "Full Name" vs
// "Candidate Name") for the same logical field. All resolution happens
// at export time — no DB migration required, existing forms and existing
// applicant records keep working.

export const FIELD_REGISTRY = [
    { key: "full_name", exportName: "Full Name", aliases: ["name", "full name", "candidate name", "applicant name", "your name"] },
    { key: "email", exportName: "Email", aliases: ["email", "email address", "official email", "e-mail", "personal email"] },
    { key: "phone", exportName: "Phone Number", aliases: ["phone", "phone number", "contact number", "mobile", "mobile number", "whatsapp number", "whatsapp"] },
    { key: "college", exportName: "College", aliases: ["college", "college name", "university", "institution", "institute"] },
    { key: "degree", exportName: "Degree", aliases: ["degree", "qualification", "education", "highest qualification"] },
    { key: "course", exportName: "Course", aliases: ["course", "course name", "specialization", "branch", "stream", "major"] },
    { key: "year_of_graduation", exportName: "Year of Graduation", aliases: ["year of graduation", "graduation year", "year of passing", "passing year", "year of passing out", "yop"] },
    { key: "resume", exportName: "Resume", aliases: ["resume", "cv", "resume link", "resume url", "cv link"] },
    { key: "portfolio", exportName: "Portfolio", aliases: ["portfolio", "portfolio url", "portfolio link", "website"] },
    { key: "linkedin", exportName: "LinkedIn", aliases: ["linkedin", "linkedin url", "linkedin profile", "linkedin link"] },
    { key: "github", exportName: "GitHub", aliases: ["github", "github profile", "github url", "github link"] },
    { key: "experience", exportName: "Experience", aliases: ["experience", "years of experience", "work experience", "yoe", "total experience"] },
    { key: "current_ctc", exportName: "Current CTC", aliases: ["current ctc", "ctc", "current salary"] },
    { key: "expected_ctc", exportName: "Expected CTC", aliases: ["expected ctc", "expected salary"] },
    { key: "notice_period", exportName: "Notice Period", aliases: ["notice period", "notice"] },
    { key: "current_location", exportName: "Current Location", aliases: ["current location", "location", "city"] },
    { key: "preferred_location", exportName: "Preferred Location", aliases: ["preferred location", "preferred city"] },
];

// Standard applicant columns → canonical keys. These live outside the
// dynamic `responses` bag so they get their own explicit mapping.
export const STANDARD_TO_KEY = {
    name: "full_name",
    email: "email",
    contact_number: "phone",
    college_name: "college",
    year_of_graduation: "year_of_graduation",
    degree: "degree",
    course: "course",
};

const normalizeLabel = (label) =>
    (label || "")
        .toLowerCase()
        .replace(/[*:().?]/g, "")
        .replace(/\s+/g, " ")
        .trim();

// Lookup: normalized alias → canonical entry
const ALIAS_INDEX = (() => {
    const m = new Map();
    FIELD_REGISTRY.forEach((entry) => {
        entry.aliases.forEach((a) => m.set(normalizeLabel(a), entry));
    });
    return m;
})();

export const resolveCanonicalKey = (label) =>
    ALIAS_INDEX.get(normalizeLabel(label)) || null;

// Unwrap the {value, other} shape used by select/degree fields when
// "Others" is chosen, so exported cells hold plain strings.
const flatten = (v) => {
    if (v == null) return "";
    if (typeof v === "object" && "value" in v) {
        return v.value === "Others" ? v.other || "" : v.value || "";
    }
    return String(v);
};

// Given one applicant row, produce a Map<canonicalKey|customLabel, value>
// where each canonical key holds the first non-empty value found among
// its aliases across standard columns + dynamic responses. Custom (non-
// canonical) response labels are kept as-is so they still export, just
// under their own column.
export const buildRowIndex = (applicant) => {
    const canonical = new Map(); // key → value
    const custom = new Map(); // label → value

    const putCanonical = (key, value) => {
        const v = flatten(value);
        if (!v) return;
        if (!canonical.has(key)) canonical.set(key, v);
    };

    // Standard columns
    Object.entries(STANDARD_TO_KEY).forEach(([col, key]) => {
        putCanonical(key, applicant[col]);
    });

    // Dynamic responses
    const responses = applicant.responses || {};
    Object.entries(responses).forEach(([label, raw]) => {
        const entry = resolveCanonicalKey(label);
        if (entry) {
            putCanonical(entry.key, raw);
        } else {
            const v = flatten(raw);
            if (v && !custom.has(label)) custom.set(label, v);
        }
    });

    return { canonical, custom };
};

// Given every applicant row, decide which canonical keys + custom labels
// actually appear in the dataset (skip empty columns). Preserves the
// registry order for canonical fields; custom fields sorted alphabetically.
export const collectExportColumns = (rows) => {
    const usedKeys = new Set();
    const usedCustom = new Set();
    rows.forEach((r) => {
        r.canonical.forEach((_, k) => usedKeys.add(k));
        r.custom.forEach((_, k) => usedCustom.add(k));
    });
    const canonicalCols = FIELD_REGISTRY
        .filter((e) => usedKeys.has(e.key))
        .map((e) => ({ kind: "canonical", key: e.key, header: e.exportName }));
    const customCols = [...usedCustom]
        .sort((a, b) => a.localeCompare(b))
        .map((label) => ({ kind: "custom", key: label, header: label }));
    return [...canonicalCols, ...customCols];
};
